/**
 * Jixxeo Emoji Duel — Best of 5 rock-paper-scissors on steroids.
 * 5 elements in a rock-paper-scissors cycle:
 *   Fire beats Nature, Nature beats Water, Water beats Fire,
 *   Lightning beats Water and Fire, Dark beats Nature and Lightning.
 * (Simplified: use RPS-5 cycle beats [i+1, i+2] mod 5.)
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MpShell } from "./_mpShell";
import { useMpClient } from "./_mpClient";

const ELEMENTS = [
  { k: "fire",  emoji: "🔥", label: "Fire",  color: "#FF003C" },
  { k: "water", emoji: "💧", label: "Water", color: "#00F0FF" },
  { k: "nature",emoji: "🌿", label: "Nature",color: "#39FF14" },
  { k: "bolt",  emoji: "⚡", label: "Bolt",  color: "#FFE300" },
  { k: "dark",  emoji: "🌑", label: "Dark",  color: "#8B5CF6" },
];

function beats(a, b) {
  if (a === b) return 0;
  const ia = ELEMENTS.findIndex((e) => e.k === a);
  const ib = ELEMENTS.findIndex((e) => e.k === b);
  const diff = (ib - ia + 5) % 5;
  return diff === 1 || diff === 2 ? 1 : -1;
}

const TARGET = 3;   // first to 3 wins

export default function JixxeoEmojiDuel() {
  const loc = useLocation();
  const challenge = new URLSearchParams(loc.search).get("challenge");
  const [stake, setStake] = useState(0);
  const [started, setStarted] = useState(!!challenge);
  const [key, setKey] = useState(0);

  const [myPick, setMyPick] = useState(null);
  const [oppPick, setOppPick] = useState(null);
  const [rounds, setRounds] = useState([]);
  const [myScore, setMyScore] = useState(0);
  const [oppScore, setOppScore] = useState(0);
  const [reveal, setReveal] = useState(false);

  const client = useMpClient({ game: "emoji-duel", stake, challenge, autoConnect: started });

  useEffect(() => {
    setMyPick(null); setOppPick(null); setRounds([]);
    setMyScore(0); setOppScore(0); setReveal(false);
  }, [key]);

  // Opponent pick arrives
  useEffect(() => {
    if (!client.lastMove) return;
    const pick = client.lastMove?.move?.pick;
    if (!pick) return;
    setOppPick(pick);
  }, [client.lastMove]);

  // When both have picked, reveal & score
  useEffect(() => {
    if (myPick && oppPick && !reveal) {
      setReveal(true);
      const r = beats(myPick, oppPick);
      setRounds((rs) => [...rs, { my: myPick, opp: oppPick, r }]);
      if (r === 1) setMyScore((s) => s + 1);
      else if (r === -1) setOppScore((s) => s + 1);
      setTimeout(() => {
        setMyPick(null); setOppPick(null); setReveal(false);
      }, 1400);
    }
  }, [myPick, oppPick, reveal]);

  // Detect match end
  useEffect(() => {
    if (myScore >= TARGET) client.sendResult("win");
    else if (oppScore >= TARGET) client.sendResult("lose");
  }, [myScore, oppScore]); // eslint-disable-line

  const play = (el) => {
    if (myPick) return;
    if (client.status !== "paired") return;
    if (myScore >= TARGET || oppScore >= TARGET) return;
    setMyPick(el.k);
    // For bot opponent, server auto-echoes a pick. For human, send.
    client.sendMove({ pick: el.k });
  };

  const isDone = myScore >= TARGET || oppScore >= TARGET;

  return (
    <MpShell title="Jixxeo Emoji Duel" accent="#FF003C" game="emoji-duel" gameSlug="jixxeo-emoji-duel"
      status={started ? client.status : "idle"}
      self={client.self} opponent={client.opponent}
      settlement={client.settlement} stake={stake} setStake={setStake}
      onFindOpponent={() => { setStarted(true); setTimeout(() => client.connect(), 0); }}
      onRematch={() => { client.close(); setKey((k) => k + 1); setStarted(false); setTimeout(() => { setStarted(true); client.connect(); }, 100); }}
      initialChallenge={challenge}>
      <div data-testid="duel-body" className="space-y-4">
        <div className="flex items-center justify-between px-3">
          <div className="text-center">
            <div className="mono text-[9px] uppercase text-[#00F0FF]">// you</div>
            <div className="font-heading font-black text-3xl">{myScore}</div>
          </div>
          <div className="text-4xl">
            {reveal
              ? `${ELEMENTS.find((e) => e.k === myPick)?.emoji} vs ${ELEMENTS.find((e) => e.k === oppPick)?.emoji}`
              : myPick ? "🎭 waiting…" : "❔ pick"}
          </div>
          <div className="text-center">
            <div className="mono text-[9px] uppercase text-[#FF003C]">// opp</div>
            <div className="font-heading font-black text-3xl">{oppScore}</div>
          </div>
        </div>

        {!isDone && (
          <div className="grid grid-cols-5 gap-2">
            {ELEMENTS.map((e) => (
              <button key={e.k} data-testid={`duel-pick-${e.k}`}
                onClick={() => play(e)}
                disabled={!!myPick || client.status !== "paired"}
                className="aspect-square rounded-xl border-2 flex flex-col items-center justify-center transition-colors disabled:opacity-40"
                style={{
                  background: `${e.color}18`,
                  borderColor: `${e.color}55`,
                }}>
                <span className="text-3xl">{e.emoji}</span>
                <span className="mono text-[8px] uppercase tracking-widest text-white/60 mt-1">{e.label}</span>
              </button>
            ))}
          </div>
        )}

        {rounds.length > 0 && (
          <div className="flex justify-center gap-1">
            {rounds.map((r, i) => (
              <span key={i} className={`w-3 h-3 rounded-full ${r.r === 1 ? "bg-[#39FF14]" : r.r === -1 ? "bg-[#FF003C]" : "bg-white/40"}`} />
            ))}
          </div>
        )}
      </div>
    </MpShell>
  );
}
